/**
 * Client typé pour les routes `/liaison` — Phase 4, incrément 4.10.2,
 * étendu **offline-first** en Phase 3.3.3.
 *
 * Deuxième sub-entité coparenting. Inclut 5 méthodes (vs 3 pour
 * journal) — la liaison a une machine d'état de signature :
 *
 *   - `listTransmissions(homeId)` : DESC created_at.
 *   - `createTransmission(homeId, input)` : kind + text + signatureRequested.
 *   - `signTransmission(id, parent)` : transition signature A/B (transmissions).
 *   - `patchTransmissionStatus(id, status)` : acceptée/refusée (demandes).
 *   - `deleteTransmission(id)`.
 *
 * Toutes les mutations passent par `sendOrQueueMutation`. La state
 * machine `/sign` est offline-able — si le user signe offline, on
 * enqueue ; le replay réémettra. Note : en cas de conflit (un autre
 * appareil a signé entre-temps), le serveur renverra **409
 * `already_signed`** et la mutation sera droppée par le replay (vu
 * comme ApiError, pas comme network error).
 */
import { apiRequest } from './client.js';
import { sendOrQueueMutation, type SendResult } from './mutationsReplay.js';

export interface ApiTransmission {
  id: string;
  homeId: string;
  kind: 'transmission' | 'demande';
  text: string;
  signatureRequested: boolean;
  status: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTransmissionInput {
  kind: 'transmission' | 'demande';
  text: string;
  /** Pertinent seulement pour kind=transmission. Default false. */
  signatureRequested?: boolean;
}

export type DemandeStatusPatch = 'acceptée' | 'refusée' | 'en attente';

export async function listTransmissions(homeId: string): Promise<ApiTransmission[]> {
  const data = await apiRequest<{ entries: ApiTransmission[] }>(`/homes/${homeId}/liaison`);
  return data.entries;
}

export async function createTransmission(
  homeId: string,
  input: CreateTransmissionInput,
  localId?: string,
): Promise<SendResult<{ entry: ApiTransmission }>> {
  return sendOrQueueMutation<{ entry: ApiTransmission }>({
    method: 'POST',
    path: `/homes/${homeId}/liaison`,
    body: input,
    ...(localId ? { localId } : {}),
  });
}

export async function signTransmission(
  id: string,
  parent: 'A' | 'B',
): Promise<SendResult<{ entry: ApiTransmission }>> {
  return sendOrQueueMutation<{ entry: ApiTransmission }>({
    method: 'POST',
    path: `/liaison/${id}/sign`,
    body: { parent },
  });
}

export async function patchTransmissionStatus(
  id: string,
  status: DemandeStatusPatch,
): Promise<SendResult<{ entry: ApiTransmission }>> {
  return sendOrQueueMutation<{ entry: ApiTransmission }>({
    method: 'PATCH',
    path: `/liaison/${id}`,
    body: { status },
  });
}

export async function deleteTransmission(id: string): Promise<SendResult<void>> {
  return sendOrQueueMutation<void>({
    method: 'DELETE',
    path: `/liaison/${id}`,
  });
}
